/**
 * CrawlDetail — Sprint E rewrite to a multi-tab Frog audit lens.
 *
 * Tabs (mirror Ahrefs/GSC structure):
 *   Summary · Issues · Pages · Links · Images · Redirects ·
 *   Canonicals · Hreflang · Schema · Mobile · Security · Orphans
 *
 * Each tab reads from its own substrate slice (gsc_pages-style):
 * crawl_issues / crawl_pages / crawl_inlinks / crawl_images /
 * crawl_redirect_chains / crawl_canonicals / crawl_hreflang /
 * crawl_structured_data / crawl_mobile / crawl_security /
 * crawl_orphan_pages.
 *
 * Pre-computed Frog Crawl Overview report (nested KV) lives on the
 * Summary tab to give the GSC-style "everything Frog computed at a
 * glance" view.
 */
import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card } from "@tremor/react";
import {
  ArrowLeft, Loader2, Search, ExternalLink, ChevronUp, ChevronDown,
  AlertTriangle, AlertCircle, Lightbulb, Image as ImageIcon,
  Link as LinkIcon, ArrowRight, Shield, Globe, Smartphone, Code,
  FileWarning, Copy as CopyIcon,
  Cpu, Gauge, Hourglass, Activity,
} from "lucide-react";
import {
  useCrawl, useCrawlPages, useProperty, useCrawlOverview,
  useCrawlIssues, useCrawlImages, useCrawlInlinks, useCrawlRedirects,
  useCrawlSubstrate, useCrawlProgressStream, formatEtaSeconds,
  type CrawlPage, type CrawlPageFilters,
  type CrawlIssue, type CrawlIssuesFilters,
  type CrawlImage, type CrawlImagesFilters,
  type CrawlInlink, type CrawlInlinksFilters,
  type CrawlRedirect, type CrawlRedirectsFilters,
  type CrawlSubstrateFilters,
} from "@/lib/queries";
import { cn } from "@/lib/utils";
import { StatusPill, formatDuration, formatDateAgo } from "./CrawlList";
import GenerateReportButton from "@/components/GenerateReportButton";


type Tab =
  | "summary" | "issues" | "pages" | "links" | "images"
  | "redirects" | "canonicals" | "hreflang" | "schema"
  | "mobile" | "security" | "orphans";


export default function CrawlDetail() {
  const { crawlId: rawId } = useParams<{ crawlId: string }>();
  const crawlId = Number(rawId);

  const crawl = useCrawl(crawlId);
  const property = useProperty(crawl.data?.property_id ?? null);
  const overview = useCrawlOverview(crawlId);

  const [tab, setTab] = useState<Tab>("summary");

  if (crawl.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }
  if (!crawl.data) {
    return <div className="text-sm text-red-600">Crawl not found.</div>;
  }
  const c = crawl.data;
  const isLive = c.status === "queued" || c.status === "running";
  const counts = overview.data?.counts ?? {};
  const auditReady = (c.status === "completed" || c.status === "timed_out");

  return (
    <div className="space-y-4">
      <div>
        <Link
          to={`/p/${c.property_id}/crawl`}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" /> Crawls for {property.data?.domain ?? `#${c.property_id}`}
        </Link>
        <div className="mt-1 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">
              Crawl #{c.id}
            </h1>
            <StatusPill status={c.status} />
          </div>
          {/* Generate Report — only meaningful once the crawl has finished
              ingesting. Hidden during running/queued/failed states because
              the substrate isn't there yet to summarize. */}
          {(c.status === "completed" || c.status === "timed_out") && (
            <GenerateReportButton crawlId={c.id} />
          )}
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          <span className="font-mono">{c.start_url}</span>
          {" · "}triggered by {c.triggered_by}
          {c.started_at && <> · started {formatDateAgo(c.started_at)}</>}
          {c.duration_s !== null && <> · ran {formatDuration(c.duration_s)}</>}
        </p>
      </div>

      {isLive && <LiveProgressPanel crawlId={c.id} />}

      <div className="flex flex-wrap items-center gap-1 border-b border-border">
        <TabButton active={tab === "summary"}    onClick={() => setTab("summary")}>Summary</TabButton>
        <TabButton active={tab === "issues"}     onClick={() => setTab("issues")}>
          <AlertTriangle className="mr-1 inline h-3 w-3" />
          Issues
          {counts.issues > 0 && <CountChip n={counts.issues} kind="warn" />}
        </TabButton>
        <TabButton active={tab === "pages"}      onClick={() => setTab("pages")}>
          Pages {c.pages_found != null && <CountChip n={c.pages_found} />}
        </TabButton>
        <TabButton active={tab === "links"}      onClick={() => setTab("links")}>
          <LinkIcon className="mr-1 inline h-3 w-3" />
          Links {counts.inlinks > 0 && <CountChip n={counts.inlinks} />}
        </TabButton>
        <TabButton active={tab === "images"}     onClick={() => setTab("images")}>
          <ImageIcon className="mr-1 inline h-3 w-3" />
          Images {counts.images > 0 && <CountChip n={counts.images} />}
        </TabButton>
        <TabButton active={tab === "redirects"}  onClick={() => setTab("redirects")}>
          <ArrowRight className="mr-1 inline h-3 w-3" />
          Redirects {counts.redirect_chains > 0 && <CountChip n={counts.redirect_chains} />}
        </TabButton>
        <TabButton active={tab === "canonicals"} onClick={() => setTab("canonicals")}>
          Canonicals {counts.canonicals > 0 && <CountChip n={counts.canonicals} />}
        </TabButton>
        <TabButton active={tab === "hreflang"}   onClick={() => setTab("hreflang")}>
          <Globe className="mr-1 inline h-3 w-3" />
          Hreflang {counts.hreflang > 0 && <CountChip n={counts.hreflang} />}
        </TabButton>
        <TabButton active={tab === "schema"}     onClick={() => setTab("schema")}>
          <Code className="mr-1 inline h-3 w-3" />
          Schema {counts.structured_data > 0 && <CountChip n={counts.structured_data} />}
        </TabButton>
        <TabButton active={tab === "mobile"}     onClick={() => setTab("mobile")}>
          <Smartphone className="mr-1 inline h-3 w-3" />
          Mobile {counts.mobile > 0 && <CountChip n={counts.mobile} />}
        </TabButton>
        <TabButton active={tab === "security"}   onClick={() => setTab("security")}>
          <Shield className="mr-1 inline h-3 w-3" />
          Security {counts.security > 0 && <CountChip n={counts.security} kind="warn" />}
        </TabButton>
        <TabButton active={tab === "orphans"}    onClick={() => setTab("orphans")}>
          Orphans {counts.orphan_pages > 0 && <CountChip n={counts.orphan_pages} kind="warn" />}
        </TabButton>
      </div>

      {tab === "summary"    && <SummaryTab crawl={c} isLive={isLive} overview={overview.data} />}
      {tab === "issues"     && (auditReady ? <IssuesTab crawlId={c.id} />     : <NotReadyCard />)}
      {tab === "pages"      && (auditReady ? <PagesTab crawlId={c.id} />      : <NotReadyCard />)}
      {tab === "links"      && (auditReady ? <LinksTab crawlId={c.id} />      : <NotReadyCard />)}
      {tab === "images"     && (auditReady ? <ImagesTab crawlId={c.id} />     : <NotReadyCard />)}
      {tab === "redirects"  && (auditReady ? <RedirectsTab crawlId={c.id} />  : <NotReadyCard />)}
      {tab === "canonicals" && (auditReady ? <CanonicalsTab crawlId={c.id} /> : <NotReadyCard />)}
      {tab === "hreflang"   && (auditReady ? <HreflangTab   crawlId={c.id} /> : <NotReadyCard />)}
      {tab === "schema"     && (auditReady ? <SchemaTab     crawlId={c.id} /> : <NotReadyCard />)}
      {tab === "mobile"     && (auditReady ? <MobileTab     crawlId={c.id} /> : <NotReadyCard />)}
      {tab === "security"   && (auditReady ? <SecurityTab   crawlId={c.id} /> : <NotReadyCard />)}
      {tab === "orphans"    && (auditReady ? <OrphansTab    crawlId={c.id} /> : <NotReadyCard />)}
    </div>
  );
}


function NotReadyCard() {
  return (
    <Card>
      <div className="text-sm text-muted-foreground">
        Audit substrate is populated when the crawl finishes. Wait for
        Frog to complete.
      </div>
    </Card>
  );
}


/**
 * Full live-progress panel for in-flight crawls. Open near the top of
 * CrawlDetail when status is queued/running. NDJSON-streams from
 * backend (which SSH-tails forge's frog.stdout.log).
 */
function LiveProgressPanel({ crawlId }: { crawlId: number }) {
  const p = useCrawlProgressStream(crawlId);
  const total = (p.completed ?? 0) + (p.waiting ?? 0);
  const pct = total > 0 ? ((p.completed ?? 0) / total) * 100 : 0;
  const queueGrowing = (p.discovery_per_s ?? 0) > (p.rate_url_per_s ?? 0);
  const queueGrowthRate = (p.discovery_per_s ?? 0) - (p.rate_url_per_s ?? 0);

  return (
    <Card>
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 animate-pulse text-blue-500" />
          <h2 className="text-sm font-semibold">Live progress</h2>
          {p.error && (
            <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[11px] text-amber-700 dark:text-amber-400">
              {p.error}
            </span>
          )}
          {!p.connected && !p.error && (
            <span className="text-[11px] text-muted-foreground">connecting…</span>
          )}
          {p.done && (
            <span className="rounded bg-emerald-500/10 px-1.5 py-0.5 text-[11px] text-emerald-700 dark:text-emerald-400">
              ✓ {p.doneStatus}
            </span>
          )}
        </div>

        {/* Filter-explosion warning banner (Phase 3 — added 2026-05-13).
            Fires when queue is growing 2×+ over a 30-sample window AND
            discovery rate is 3×+ completion rate AND >5k URLs discovered.
            Hardcoded heuristic catches the gregorco-class problem early. */}
        {p.explosion_warning && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-800 dark:text-amber-200">
            <div className="flex items-center gap-1.5 font-medium">
              <AlertTriangle className="h-3.5 w-3.5" />
              Filter explosion detected — the queue is growing faster than the crawler can drain it.
            </div>
            <div className="mt-1 text-amber-700 dark:text-amber-300">
              {p.telemetry.queue_growth_ratio && (
                <span>Queue is <strong>{p.telemetry.queue_growth_ratio}×</strong> bigger than 30s ago. </span>
              )}
              {p.telemetry.discovery_to_completion_ratio && (
                <span>Discovery is <strong>{p.telemetry.discovery_to_completion_ratio}×</strong> the crawl rate. </span>
              )}
              {p.telemetry.total_discovered && (
                <span>{p.telemetry.total_discovered.toLocaleString()} URLs discovered already. </span>
              )}
            </div>
            <div className="mt-1.5 text-amber-700 dark:text-amber-300">
              Likely cause: WordPress / WooCommerce filter combinatorics (
              <span className="font-mono">?filter_color=…&filter_size=…</span>
              ). Cancel this crawl, open Property → Settings → Crawl scope,
              add the offending pattern to <strong>Exclude URL patterns</strong>,
              then restart.
            </div>
          </div>
        )}

        {p.completed != null && (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              <LiveStat
                label="Completed"
                value={(p.completed ?? 0).toLocaleString()}
                hint={`${(p.pct ?? 0).toFixed(1)}% of discovered queue`}
              />
              <LiveStat
                label="In queue"
                value={(p.waiting ?? 0).toLocaleString()}
                hint={`${total.toLocaleString()} total discovered`}
                kind={queueGrowing ? "warn" : undefined}
              />
              <LiveStat
                icon={Cpu}
                label="Workers active"
                value={`${p.active ?? 0}`}
                hint={p.active === 30 ? "saturated (max)" : "of 30 max"}
              />
              <LiveStat
                icon={Gauge}
                label="Crawl rate"
                value={p.rate_url_per_s != null ? `${p.rate_url_per_s}/s` : "—"}
                hint={p.discovery_per_s != null ? `${p.discovery_per_s}/s discovery` : ""}
              />
              <LiveStat
                icon={Hourglass}
                label="ETA"
                value={formatEtaSeconds(p.eta_seconds)}
                hint={
                  queueGrowing
                    ? `queue +${queueGrowthRate.toFixed(1)}/s — fuzzy`
                    : "queue saturating"
                }
                kind={queueGrowing ? "warn" : undefined}
              />
            </div>

            <div className="space-y-1">
              <div className="h-1.5 w-full overflow-hidden rounded bg-muted">
                <div
                  className="h-full bg-blue-500 transition-all"
                  style={{ width: `${Math.min(100, Math.max(2, pct))}%` }}
                />
              </div>
              {queueGrowing && (
                <p className="text-[11px] text-amber-700 dark:text-amber-400">
                  ⚠ Queue growing faster than completion rate. Either the
                  link graph hasn't saturated yet (normal early in a crawl)
                  or this site has crawl-trap URLs (faceted nav, calendar
                  pages, etc.). ETA is fuzzy until this stabilizes.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </Card>
  );
}


/**
 * Stat-cell variant used by LiveProgressPanel (renamed from Stat to
 * avoid collision with the SummaryTab's Stat component further down
 * — same name, different shape).
 */
function LiveStat({
  icon: Icon, label, value, hint, kind,
}: {
  icon?: typeof Cpu;
  label: string;
  value: string;
  hint?: string;
  kind?: "warn";
}) {
  return (
    <div>
      <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </div>
      <div className={cn(
        "mt-0.5 font-mono text-lg",
        kind === "warn" && "text-amber-700 dark:text-amber-400",
      )}>
        {value}
      </div>
      {hint && (
        <div className="text-[10px] text-muted-foreground">{hint}</div>
      )}
    </div>
  );
}


function CountChip({ n, kind }: { n: number; kind?: "warn" }) {
  return (
    <span className={cn(
      "ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium",
      kind === "warn"
        ? "bg-amber-500/10 text-amber-700 dark:text-amber-400"
        : "bg-muted text-muted-foreground",
    )}>
      {n.toLocaleString()}
    </span>
  );
}


function TabButton({ active, onClick, children }: {
  active: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "border-b-2 px-3 py-2 text-sm font-medium transition-colors",
        active
          ? "border-primary text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}


// ─── Summary tab ───────────────────────────────────────────────────


function SummaryTab({ crawl: c, isLive, overview }: {
  crawl: ReturnType<typeof useCrawl>["data"];
  isLive: boolean;
  overview: ReturnType<typeof useCrawlOverview>["data"];
}) {
  if (!c) return null;
  const ov = overview?.overview;

  return (
    <div className="space-y-4">
      {isLive && (
        <Card className="border-blue-500/40 bg-blue-500/5">
          <div className="flex items-center gap-2 text-sm">
            <Loader2 className="h-4 w-4 animate-spin text-blue-700 dark:text-blue-400" />
            <span className="font-medium">Crawl in progress.</span>
            <span className="text-muted-foreground">
              Frog can take from minutes to several hours depending on site
              size. This page polls every 5s.
            </span>
          </div>
        </Card>
      )}

      {c.error && (
        <Card className="border-red-500/40 bg-red-500/5">
          <div className="text-sm text-red-700 dark:text-red-400">
            <div className="font-medium">Error</div>
            <div className="mt-1 font-mono text-xs">{c.error}</div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Pages found"          value={c.pages_found ?? "—"} />
        <Stat label="Broken (4xx/5xx)"     value={c.broken_count ?? "—"}
              kind={(c.broken_count ?? 0) > 0 ? "warn" : "ok"} />
        <Stat label="Missing title"        value={c.missing_title_count ?? "—"}
              kind={(c.missing_title_count ?? 0) > 0 ? "warn" : "ok"} />
        <Stat label="Missing meta"         value={c.missing_meta_count ?? "—"}
              kind={(c.missing_meta_count ?? 0) > 0 ? "warn" : "ok"} />
        <Stat label="Duplicate titles"     value={c.duplicate_title_count ?? "—"}
              kind={(c.duplicate_title_count ?? 0) > 0 ? "warn" : "ok"} />
        <Stat label="Frog exit code"       value={c.frog_exit_code ?? "—"}
              kind={c.frog_exit_code === 0 ? "ok" : c.frog_exit_code !== null ? "warn" : undefined} />
        <Stat label="Duration"             value={c.duration_s ? formatDuration(c.duration_s) : "—"} />
        <Stat label="Max URLs"             value={c.max_urls.toLocaleString()} />
      </div>

      {c.status_code_counts && Object.keys(c.status_code_counts).length > 0 && (
        <Card>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Status code breakdown
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {Object.entries(c.status_code_counts)
              .sort((a, b) => Number(a[0]) - Number(b[0]))
              .map(([code, n]) => (
                <div key={code} className="rounded-md border border-border bg-muted/30 px-3 py-2">
                  <div className="font-mono text-sm">{code}</div>
                  <div className="text-xs text-muted-foreground">{n.toLocaleString()} pages</div>
                </div>
              ))}
          </div>
        </Card>
      )}

      {ov && Object.keys(ov).length > 0 && (
        <Card>
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">Frog Crawl Overview</h3>
            <span className="text-[11px] text-muted-foreground">
              Pre-computed by Screaming Frog at the end of the crawl
            </span>
          </div>
          <div className="mt-3 space-y-3">
            {Object.entries(ov).map(([section, fields]) => (
              <div key={section}>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {section}
                </div>
                <div className="mt-1 grid grid-cols-1 gap-1 sm:grid-cols-2 lg:grid-cols-3">
                  {Object.entries(fields).map(([field, val]) => (
                    <div key={field} className="flex items-baseline justify-between gap-2 rounded border border-border/50 bg-muted/20 px-2 py-1">
                      <span className="text-[11px] text-muted-foreground">{field}</span>
                      <span className="font-mono text-xs">{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}


function Stat({ label, value, kind }: {
  label: string; value: string | number;
  kind?: "ok" | "warn";
}) {
  return (
    <div className="rounded-md border border-border bg-muted/30 px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn(
        "mt-0.5 text-lg font-semibold",
        kind === "ok"   && "text-emerald-700 dark:text-emerald-400",
        kind === "warn" && "text-amber-700 dark:text-amber-400",
      )}>
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
    </div>
  );
}


// ─── Issues tab — THE 300+ catalog ─────────────────────────────────


function IssuesTab({ crawlId }: { crawlId: number }) {
  const [filters, setFilters] = useState<CrawlIssuesFilters>({
    sort: "urls_count", sort_dir: "desc", limit: 200, offset: 0,
  });
  const issues = useCrawlIssues(crawlId, filters);

  const setF = (patch: Partial<CrawlIssuesFilters>) =>
    setFilters((f) => ({ ...f, offset: 0, ...patch }));

  const byType = issues.data?.by_type ?? {};
  const byPriority = issues.data?.by_priority ?? {};

  return (
    <div className="space-y-3">
      <Card>
        <div className="flex flex-wrap items-center gap-1.5">
          <FilterPill active={!filters.issue_type} onClick={() => setF({ issue_type: undefined })}>
            All
            {(byType.Issue ?? 0) + (byType.Warning ?? 0) + (byType.Opportunity ?? 0) > 0 && (
              <CountChip n={(byType.Issue ?? 0) + (byType.Warning ?? 0) + (byType.Opportunity ?? 0)} />
            )}
          </FilterPill>
          <FilterPill active={filters.issue_type === "Issue"} onClick={() => setF({ issue_type: "Issue" })}>
            <AlertCircle className="mr-1 inline h-3 w-3" /> Issue
            <CountChip n={byType.Issue ?? 0} kind="warn" />
          </FilterPill>
          <FilterPill active={filters.issue_type === "Warning"} onClick={() => setF({ issue_type: "Warning" })}>
            <AlertTriangle className="mr-1 inline h-3 w-3" /> Warning
            <CountChip n={byType.Warning ?? 0} />
          </FilterPill>
          <FilterPill active={filters.issue_type === "Opportunity"} onClick={() => setF({ issue_type: "Opportunity" })}>
            <Lightbulb className="mr-1 inline h-3 w-3" /> Opportunity
            <CountChip n={byType.Opportunity ?? 0} />
          </FilterPill>
          <span className="mx-2 h-5 w-px bg-border" />
          <FilterPill active={!filters.priority} onClick={() => setF({ priority: undefined })}>
            All priority
          </FilterPill>
          {(["High", "Medium", "Low"] as const).map((p) => (
            <FilterPill key={p} active={filters.priority === p} onClick={() => setF({ priority: p })}>
              {p}
              <CountChip n={byPriority[p] ?? 0} kind={p === "High" ? "warn" : undefined} />
            </FilterPill>
          ))}
        </div>
        <div className="mt-2 relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="filter issue name or description…"
            value={filters.search ?? ""}
            onChange={(e) => setF({ search: e.target.value || undefined })}
            className="w-full rounded-md border border-input bg-background pl-7 pr-2 py-1.5 text-xs"
          />
        </div>
        {issues.data && (
          <div className="mt-2 text-xs text-muted-foreground">
            Showing {issues.data.items.length.toLocaleString()} of {issues.data.total.toLocaleString()} issues
          </div>
        )}
      </Card>

      <Card className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="border-b border-border text-left">
            <tr>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Issue</th>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Type</th>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Priority</th>
              <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">URLs</th>
              <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">% of total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {issues.isLoading && <tr><td colSpan={5} className="px-2 py-4 text-center text-muted-foreground">Loading…</td></tr>}
            {issues.data?.items.map((i) => <IssueRow key={i.id} crawlId={crawlId} issue={i} />)}
            {issues.data && issues.data.items.length === 0 && (
              <tr><td colSpan={5} className="px-2 py-4 text-center text-muted-foreground">
                No issues match. {issues.data.total === 0 && "Frog returned no issues — either the crawl is fresh and the issues export hasn't been ingested, or the site is genuinely clean."}
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}


function IssueRow({ crawlId, issue }: { crawlId: number; issue: CrawlIssue }) {
  const [open, setOpen] = useState(false);
  const typeColor =
    issue.issue_type === "Issue" ? "bg-red-500/10 text-red-700 dark:text-red-400" :
    issue.issue_type === "Warning" ? "bg-amber-500/10 text-amber-700 dark:text-amber-400" :
    issue.issue_type === "Opportunity" ? "bg-blue-500/10 text-blue-700 dark:text-blue-400" :
    "bg-muted text-muted-foreground";
  const priorityColor =
    issue.priority === "High" ? "text-red-700 dark:text-red-400" :
    issue.priority === "Medium" ? "text-amber-700 dark:text-amber-400" :
    "text-muted-foreground";

  // Drill-down: fetch per-URL detail when expanded
  const details = useCrawlIssues(
    crawlId,
    { issue_slug: issue.issue_slug ?? undefined, limit: 200, offset: 0 },
  );
  // Disable the query until the row is opened (query is cached after)
  const detailItems = open ? details.data?.items ?? [] : [];

  return (
    <>
      <tr className="hover:bg-accent/40 cursor-pointer" onClick={() => setOpen(!open)}>
        <td className="px-2 py-1.5 align-top">
          <div className="flex items-start gap-1">
            {open ? <ChevronDown className="mt-0.5 h-3 w-3" /> : <ChevronUp className="mt-0.5 h-3 w-3 rotate-90" />}
            <span className="font-medium">{issue.issue_name}</span>
          </div>
        </td>
        <td className="px-2 py-1.5 align-top">
          <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-medium", typeColor)}>
            {issue.issue_type}
          </span>
        </td>
        <td className={cn("px-2 py-1.5 align-top font-medium", priorityColor)}>
          {issue.priority ?? "—"}
        </td>
        <td className="px-2 py-1.5 text-right align-top font-mono">
          {issue.urls_count?.toLocaleString() ?? "—"}
        </td>
        <td className="px-2 py-1.5 text-right align-top font-mono text-muted-foreground">
          {issue.pct_of_total != null ? `${issue.pct_of_total.toFixed(1)}%` : "—"}
        </td>
      </tr>
      {open && (
        <tr className="bg-muted/20">
          <td colSpan={5} className="px-4 py-3">
            {issue.description && (
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Description
                </div>
                <p className="mt-0.5 text-xs leading-snug">{issue.description}</p>
              </div>
            )}
            {issue.how_to_fix && (
              <div className="mt-2">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  How to fix
                </div>
                <p className="mt-0.5 text-xs leading-snug">{issue.how_to_fix}</p>
              </div>
            )}
            <div className="mt-3">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Affected URLs
              </div>
              {!issue.issue_slug ? (
                <p className="mt-0.5 text-[11px] italic text-muted-foreground">
                  No slug for this issue — drill-down unavailable. (This row was
                  derived from the summary report without per-URL detail. Re-run
                  the crawl after the next Frog upgrade.)
                </p>
              ) : details.isLoading ? (
                <p className="mt-0.5 text-[11px] italic text-muted-foreground">Loading…</p>
              ) : detailItems.length === 0 ? (
                <p className="mt-0.5 text-[11px] italic text-muted-foreground">
                  No per-URL detail was exported by Frog for this issue. The
                  count of {issue.urls_count} URLs is in the summary but Frog
                  didn't write a corresponding detail CSV. (Common for some
                  issue types — Frog only emits detail CSVs for issues that
                  fire on at least one URL during the crawl run.)
                </p>
              ) : (
                <div className="mt-1 overflow-x-auto rounded-md border border-border">
                  <table className="min-w-full text-[11px]">
                    <thead className="border-b border-border bg-muted/30">
                      <tr>
                        <th className="px-2 py-1 text-left font-medium text-muted-foreground">URL</th>
                        <th className="px-2 py-1 text-left font-medium text-muted-foreground">Detail</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {detailItems.slice(0, 100).map((d) => (
                        <tr key={d.id}>
                          <td className="px-2 py-1 align-top font-mono">
                            <a href={d.url ?? "#"} target="_blank" rel="noreferrer"
                               className="hover:underline" title={d.url ?? ""}>
                              {stripScheme(d.url ?? "")}
                            </a>
                          </td>
                          <td className="px-2 py-1 align-top">
                            {d.raw && Object.keys(d.raw).length > 0 ? (
                              <RawDetail raw={d.raw} url={d.url ?? ""} />
                            ) : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {detailItems.length > 100 && (
                    <div className="border-t border-border bg-muted/20 px-2 py-1 text-[10px] text-muted-foreground">
                      Showing 100 of {detailItems.length.toLocaleString()} URLs
                    </div>
                  )}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}


function RawDetail({ raw, url }: { raw: Record<string, unknown>; url: string }) {
  // Pull a few useful keys to display compactly. Skip "Address" since it's the URL we already show.
  const interesting = Object.entries(raw)
    .filter(([k]) => k !== "Address" && k !== "URL")
    .slice(0, 4);
  if (interesting.length === 0) return <span className="text-muted-foreground">{url}</span>;
  return (
    <div className="space-y-0.5">
      {interesting.map(([k, v]) => (
        <div key={k} className="font-mono text-[10px]">
          <span className="text-muted-foreground">{k}:</span> {String(v).slice(0, 100)}
        </div>
      ))}
    </div>
  );
}


function FilterPill({ active, onClick, children }: {
  active: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button type="button" onClick={onClick}
      className={cn(
        "inline-flex items-center gap-0.5 rounded-md px-2.5 py-1 text-xs font-medium",
        active
          ? "bg-primary text-primary-foreground"
          : "bg-muted text-muted-foreground hover:bg-accent hover:text-foreground",
      )}>
      {children}
    </button>
  );
}


// ─── Pages tab (existing) ─────────────────────────────────────────


function PagesTab({ crawlId }: { crawlId: number }) {
  const [filters, setFilters] = useState<CrawlPageFilters>({
    sort: "inlinks", sort_dir: "desc", limit: 100, offset: 0,
  });
  const pages = useCrawlPages(crawlId, filters);
  const setF = (patch: Partial<CrawlPageFilters>) =>
    setFilters((f) => ({ ...f, offset: 0, ...patch }));

  return (
    <div className="space-y-3">
      <Card>
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input type="text" placeholder="filter URL by substring…"
              value={filters.search ?? ""}
              onChange={(e) => setF({ search: e.target.value || undefined })}
              className="w-full rounded-md border border-input bg-background pl-7 pr-2 py-1.5 text-xs" />
          </div>
          <select value={filters.status_class ?? ""}
            onChange={(e) => setF({ status_class: (e.target.value || undefined) as CrawlPageFilters["status_class"] })}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-xs">
            <option value="">All statuses</option>
            <option value="2xx">2xx</option>
            <option value="3xx">3xx</option>
            <option value="4xx">4xx</option>
            <option value="5xx">5xx</option>
          </select>
          <FilterTriToggle label="Title" value={filters.missing_title} onTrue="missing" onFalse="present"
            onChange={(v) => setF({ missing_title: v })} />
          <FilterTriToggle label="Meta" value={filters.missing_meta} onTrue="missing" onFalse="present"
            onChange={(v) => setF({ missing_meta: v })} />
          <select value={filters.indexability ?? ""}
            onChange={(e) => setF({ indexability: (e.target.value || undefined) as CrawlPageFilters["indexability"] })}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-xs">
            <option value="">All indexability</option>
            <option value="Indexable">Indexable</option>
            <option value="Non-Indexable">Non-Indexable</option>
          </select>
        </div>
        {pages.data && (
          <div className="mt-2 text-xs text-muted-foreground">
            Showing {pages.data.items.length.toLocaleString()} of {pages.data.total.toLocaleString()}
          </div>
        )}
      </Card>

      <Card className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="border-b border-border text-left">
            <tr>
              <SortHeader label="URL" col="url" filters={filters} setF={setF} className="w-[28%]"/>
              <SortHeader label="Status" col="status" filters={filters} setF={setF} />
              <SortHeader label="Title" col="title" filters={filters} setF={setF} className="w-[22%]"/>
              <SortHeader label="Words" col="word_count" filters={filters} setF={setF} align="right"/>
              <SortHeader label="In" col="inlinks" filters={filters} setF={setF} align="right"/>
              <SortHeader label="Out" col="outlinks" filters={filters} setF={setF} align="right"/>
              <SortHeader label="Depth" col="crawl_depth" filters={filters} setF={setF} align="right"/>
              <SortHeader label="RT(ms)" col="response_time" filters={filters} setF={setF} align="right"/>
              <th className="px-2 py-1.5">Indexability</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {pages.isLoading && <tr><td colSpan={9} className="px-2 py-4 text-center text-muted-foreground">Loading…</td></tr>}
            {pages.data?.items.map((p) => <PageRow key={p.id} page={p} />)}
            {pages.data && pages.data.items.length === 0 && (
              <tr><td colSpan={9} className="px-2 py-4 text-center text-muted-foreground">No pages match.</td></tr>
            )}
          </tbody>
        </table>
        <Paginator data={pages.data ? { total: pages.data.total } : undefined} filters={filters} setFilters={setFilters} />
      </Card>
    </div>
  );
}


// ─── Links tab ─────────────────────────────────────────────────────


function LinksTab({ crawlId }: { crawlId: number }) {
  // Defaults: link_type=Hyperlink (skip image/CSS/JS noise) + exclude_self
  // (skip homepage→homepage navigation links). Backend uses these same
  // defaults — explicit here so the dropdown reflects the active state.
  const [filters, setFilters] = useState<CrawlInlinksFilters>({
    limit: 100, offset: 0,
    link_type: "Hyperlink",
    exclude_self: true,
  });
  const inlinks = useCrawlInlinks(crawlId, filters);
  const setF = (patch: Partial<CrawlInlinksFilters>) =>
    setFilters((f) => ({ ...f, offset: 0, ...patch }));

  return (
    <div className="space-y-3">
      <Card>
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input type="text" placeholder="filter source/target/anchor…"
              value={filters.search ?? ""}
              onChange={(e) => setF({ search: e.target.value || undefined })}
              className="w-full rounded-md border border-input bg-background pl-7 pr-2 py-1.5 text-xs" />
          </div>
          <select
            value={filters.link_type ?? "Hyperlink"}
            onChange={(e) => setF({ link_type: e.target.value })}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-xs"
            title="Frog tracks every in-page reference as an 'inlink' — Hyperlink hides Image/CSS/JS/Hreflang/Canonical noise."
          >
            <option value="Hyperlink">Hyperlinks (default)</option>
            <option value="Image">Image refs</option>
            <option value="CSS">CSS refs</option>
            <option value="JavaScript">JavaScript refs</option>
            <option value="HTML Hreflang">Hreflang refs</option>
            <option value="HTML Canonical">Canonical refs</option>
            <option value="Iframe">Iframe refs</option>
            <option value="HTML Rel Next">Rel Next/Prev</option>
            <option value="HTTP Redirect">HTTP Redirects</option>
            <option value="all">All types (raw)</option>
          </select>
          <SrcFilterBtn label="Broken targets only"
            active={!!filters.only_broken}
            onClick={() => setF({ only_broken: filters.only_broken ? undefined : true })} />
          <SrcFilterBtn label="Nofollow only"
            active={!!filters.only_nofollow}
            onClick={() => setF({ only_nofollow: filters.only_nofollow ? undefined : true })} />
          <SrcFilterBtn label="Self-links"
            active={filters.exclude_self === false}
            onClick={() => setF({ exclude_self: filters.exclude_self === false ? true : false })}
          />
        </div>
        {inlinks.data && (
          <div className="mt-2 text-xs text-muted-foreground">
            Showing {inlinks.data.items.length.toLocaleString()} of {inlinks.data.total.toLocaleString()} {filters.link_type === "all" ? "" : (filters.link_type ?? "Hyperlink").toLowerCase()} links
            {filters.exclude_self !== false && " (self-links hidden)"}
          </div>
        )}
      </Card>

      <Card className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="border-b border-border text-left">
            <tr>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">From</th>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Anchor</th>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">To</th>
              <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Status</th>
              <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Follow</th>
              <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Type</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {inlinks.isLoading && <tr><td colSpan={6} className="px-2 py-4 text-center text-muted-foreground">Loading…</td></tr>}
            {inlinks.data?.items.map((l) => <InlinkRow key={l.id} link={l} />)}
            {inlinks.data && inlinks.data.items.length === 0 && (
              <tr><td colSpan={6} className="px-2 py-4 text-center text-muted-foreground">No links match.</td></tr>
            )}
          </tbody>
        </table>
        <Paginator data={inlinks.data ? { total: inlinks.data.total } : undefined} filters={filters} setFilters={setFilters} />
      </Card>
    </div>
  );
}


function InlinkRow({ link: l }: { link: CrawlInlink }) {
  return (
    <tr className="hover:bg-accent/40">
      <td className="px-2 py-1.5 align-top font-mono text-[11px] max-w-[28%] truncate">
        <a href={l.source_url} target="_blank" rel="noreferrer" className="hover:underline" title={l.source_url}>
          {stripScheme(l.source_url)}
        </a>
      </td>
      <td className="px-2 py-1.5 align-top max-w-[20%] truncate" title={l.anchor ?? ""}>
        {l.anchor || <span className="italic text-muted-foreground">empty</span>}
      </td>
      <td className="px-2 py-1.5 align-top font-mono text-[11px] max-w-[28%] truncate">
        <a href={l.target_url} target="_blank" rel="noreferrer" className="hover:underline" title={l.target_url}>
          {stripScheme(l.target_url)}
        </a>
      </td>
      <td className="px-2 py-1.5 text-right align-top">
        {l.status_code != null && (
          <span className={cn(
            "rounded px-1 font-mono",
            l.status_code >= 500 ? "bg-red-500/10 text-red-700 dark:text-red-400" :
            l.status_code >= 400 ? "bg-amber-500/10 text-amber-700 dark:text-amber-400" :
            l.status_code >= 300 ? "bg-blue-500/10 text-blue-700 dark:text-blue-400" :
            "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
          )}>{l.status_code}</span>
        )}
      </td>
      <td className="px-2 py-1.5 text-right align-top text-muted-foreground">{l.follow ?? "—"}</td>
      <td className="px-2 py-1.5 text-right align-top text-muted-foreground">{l.link_type ?? "—"}</td>
    </tr>
  );
}


// ─── Images tab ────────────────────────────────────────────────────


function ImagesTab({ crawlId }: { crawlId: number }) {
  const [filters, setFilters] = useState<CrawlImagesFilters>({
    sort: "size_bytes", sort_dir: "desc", limit: 100, offset: 0,
  });
  const images = useCrawlImages(crawlId, filters);
  const setF = (patch: Partial<CrawlImagesFilters>) =>
    setFilters((f) => ({ ...f, offset: 0, ...patch }));

  return (
    <div className="space-y-3">
      <Card>
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input type="text" placeholder="filter image URL or source page…"
              value={filters.search ?? ""}
              onChange={(e) => setF({ search: e.target.value || undefined })}
              className="w-full rounded-md border border-input bg-background pl-7 pr-2 py-1.5 text-xs" />
          </div>
          <FilterTriToggle label="Alt"
            value={filters.missing_alt}
            onTrue="missing"
            onFalse="present"
            onChange={(v) => setF({ missing_alt: v })} />
          <SrcFilterBtn label="Broken only"
            active={!!filters.only_broken}
            onClick={() => setF({ only_broken: filters.only_broken ? undefined : true })} />
          <select value={filters.min_size_kb ?? ""}
            onChange={(e) => setF({ min_size_kb: e.target.value ? Number(e.target.value) : undefined })}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-xs">
            <option value="">Any size</option>
            <option value="100">≥ 100 KB</option>
            <option value="500">≥ 500 KB</option>
            <option value="1000">≥ 1 MB</option>
          </select>
        </div>
        {images.data && (
          <div className="mt-2 text-xs text-muted-foreground">
            {images.data.items.length.toLocaleString()} of {images.data.total.toLocaleString()} images
          </div>
        )}
      </Card>

      <Card className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="border-b border-border text-left">
            <tr>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Image</th>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Source page</th>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Alt text</th>
              <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Size</th>
              <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {images.isLoading && <tr><td colSpan={5} className="px-2 py-4 text-center text-muted-foreground">Loading…</td></tr>}
            {images.data?.items.map((img) => <ImageRow key={img.id} img={img} />)}
            {images.data && images.data.items.length === 0 && (
              <tr><td colSpan={5} className="px-2 py-4 text-center text-muted-foreground">No images match.</td></tr>
            )}
          </tbody>
        </table>
        <Paginator data={images.data ? { total: images.data.total } : undefined} filters={filters} setFilters={setFilters} />
      </Card>
    </div>
  );
}


function ImageRow({ img }: { img: CrawlImage }) {
  return (
    <tr className="hover:bg-accent/40">
      <td className="px-2 py-1.5 align-top font-mono text-[11px] max-w-[35%] truncate">
        <a href={img.image_url} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-1 hover:underline" title={img.image_url}>
          <ImageIcon className="h-3 w-3 shrink-0 text-muted-foreground" />
          {stripScheme(img.image_url)}
        </a>
      </td>
      <td className="px-2 py-1.5 align-top font-mono text-[11px] max-w-[25%] truncate">
        {img.source_url ? (
          <a href={img.source_url} target="_blank" rel="noreferrer" className="hover:underline" title={img.source_url}>
            {stripScheme(img.source_url)}
          </a>
        ) : "—"}
      </td>
      <td className="px-2 py-1.5 align-top max-w-[20%] truncate">
        {img.alt_text ? img.alt_text : <span className="italic text-amber-700 dark:text-amber-400">missing</span>}
      </td>
      <td className="px-2 py-1.5 text-right align-top font-mono">
        {img.size_bytes != null ? `${(img.size_bytes / 1024).toFixed(1)} KB` : "—"}
      </td>
      <td className="px-2 py-1.5 text-right align-top">
        {img.status_code != null && (
          <span className={cn(
            "rounded px-1 font-mono",
            img.status_code >= 400 ? "bg-red-500/10 text-red-700 dark:text-red-400" :
            "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
          )}>{img.status_code}</span>
        )}
      </td>
    </tr>
  );
}


// ─── Redirects tab ────────────────────────────────────────────────


function RedirectsTab({ crawlId }: { crawlId: number }) {
  const [filters, setFilters] = useState<CrawlRedirectsFilters>({
    limit: 100, offset: 0,
  });
  const redirects = useCrawlRedirects(crawlId, filters);
  const setF = (patch: Partial<CrawlRedirectsFilters>) =>
    setFilters((f) => ({ ...f, offset: 0, ...patch }));

  return (
    <div className="space-y-3">
      {redirects.data && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Stat label="Total chains"     value={redirects.data.total} />
          <Stat label="Long chains (3+)" value={redirects.data.long_chain_count}
                kind={redirects.data.long_chain_count > 0 ? "warn" : "ok"} />
          <Stat label="Loops"            value={redirects.data.loop_count}
                kind={redirects.data.loop_count > 0 ? "warn" : "ok"} />
        </div>
      )}

      <Card>
        <div className="flex flex-wrap gap-2">
          <SrcFilterBtn label="Loops only"
            active={!!filters.only_loops}
            onClick={() => setF({ only_loops: filters.only_loops ? undefined : true })} />
          <select value={filters.min_hops ?? ""}
            onChange={(e) => setF({ min_hops: e.target.value ? Number(e.target.value) : undefined })}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-xs">
            <option value="">Any chain length</option>
            <option value="2">≥ 2 hops</option>
            <option value="3">≥ 3 hops</option>
            <option value="5">≥ 5 hops</option>
          </select>
        </div>
      </Card>

      <Card className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="border-b border-border text-left">
            <tr>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Source</th>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Final URL</th>
              <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Hops</th>
              <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Final status</th>
              <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Loop?</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {redirects.isLoading && <tr><td colSpan={5} className="px-2 py-4 text-center text-muted-foreground">Loading…</td></tr>}
            {redirects.data?.items.map((r) => <RedirectRow key={r.id} r={r} />)}
            {redirects.data && redirects.data.items.length === 0 && (
              <tr><td colSpan={5} className="px-2 py-4 text-center text-muted-foreground">No redirect chains.</td></tr>
            )}
          </tbody>
        </table>
        <Paginator data={redirects.data ? { total: redirects.data.total } : undefined} filters={filters} setFilters={setFilters} />
      </Card>
    </div>
  );
}


function RedirectRow({ r }: { r: CrawlRedirect }) {
  return (
    <tr className={cn("hover:bg-accent/40", r.has_loop && "bg-red-500/5")}>
      <td className="px-2 py-1.5 align-top font-mono text-[11px] max-w-[36%] truncate">
        <a href={r.source_url} target="_blank" rel="noreferrer" className="hover:underline" title={r.source_url}>
          {stripScheme(r.source_url)}
        </a>
      </td>
      <td className="px-2 py-1.5 align-top font-mono text-[11px] max-w-[36%] truncate">
        {r.final_url ? (
          <a href={r.final_url} target="_blank" rel="noreferrer" className="hover:underline" title={r.final_url}>
            {stripScheme(r.final_url)}
          </a>
        ) : "—"}
      </td>
      <td className="px-2 py-1.5 text-right align-top font-mono">
        {r.hops != null ? (
          <span className={cn((r.hops ?? 0) >= 3 ? "text-amber-700 dark:text-amber-400" : "")}>
            {r.hops}
          </span>
        ) : "—"}
      </td>
      <td className="px-2 py-1.5 text-right align-top">
        {r.final_status != null && (
          <span className={cn(
            "rounded px-1 font-mono",
            r.final_status >= 400 ? "bg-red-500/10 text-red-700 dark:text-red-400" :
            "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
          )}>{r.final_status}</span>
        )}
      </td>
      <td className="px-2 py-1.5 text-right align-top">
        {r.has_loop ? <span className="text-red-700 dark:text-red-400 font-semibold">⚠ loop</span> : "—"}
      </td>
    </tr>
  );
}


// ─── Substrate tabs: canonicals / hreflang / schema / mobile / security / orphans ─


function CanonicalsTab({ crawlId }: { crawlId: number }) {
  const [filters, setFilters] = useState<CrawlSubstrateFilters>({ limit: 200, offset: 0 });
  const data = useCrawlSubstrate(crawlId, "canonicals", filters);
  const setF = (patch: Partial<CrawlSubstrateFilters>) =>
    setFilters((f) => ({ ...f, offset: 0, ...patch }));

  return (
    <div className="space-y-3">
      <Card>
        <div className="flex items-center gap-2">
          <CopyIcon className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">Canonicals</h3>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Per-URL canonical config. Pages with missing/conflicting canonicals
          surface here (Frog also reports them on the Issues tab).
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input type="text" placeholder="filter URL…"
              value={filters.search ?? ""}
              onChange={(e) => setF({ search: e.target.value || undefined })}
              className="w-full rounded-md border border-input bg-background pl-7 pr-2 py-1.5 text-xs" />
          </div>
          <SrcFilterBtn label="Missing canonical only"
            active={!!filters.only_missing}
            onClick={() => setF({ only_missing: filters.only_missing ? undefined : true })} />
        </div>
        {data.data && <RowCount n={data.data.items.length} total={data.data.total} />}
      </Card>

      <Card className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="border-b border-border text-left">
            <tr>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">URL</th>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Canonical</th>
              <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Occurrences</th>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Indexability</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.isLoading && <tr><td colSpan={4} className="px-2 py-4 text-center text-muted-foreground">Loading…</td></tr>}
            {data.data?.items.map((r) => (
              <tr key={r.id} className={cn("hover:bg-accent/40", (!r.canonical || r.canonical === "") && "bg-amber-500/5")}>
                <td className="px-2 py-1.5 align-top font-mono text-[11px] max-w-[35%] truncate">
                  <UrlLink url={r.url} />
                </td>
                <td className="px-2 py-1.5 align-top font-mono text-[11px] max-w-[35%] truncate">
                  {r.canonical
                    ? <UrlLink url={r.canonical} />
                    : <span className="italic text-amber-700 dark:text-amber-400">missing</span>}
                </td>
                <td className="px-2 py-1.5 text-right align-top font-mono">
                  {r.occurrences != null ? (r.occurrences > 1 ? <span className="text-red-700 dark:text-red-400">{r.occurrences}</span> : r.occurrences) : "—"}
                </td>
                <td className="px-2 py-1.5 align-top">
                  <div>{r.indexability ?? "—"}</div>
                  {r.indexability_status && <div className="text-[10px] text-muted-foreground">{r.indexability_status}</div>}
                </td>
              </tr>
            ))}
            {data.data && data.data.items.length === 0 && (
              <tr><td colSpan={4} className="px-2 py-4 text-center text-muted-foreground">No canonicals match.</td></tr>
            )}
          </tbody>
        </table>
        <Paginator data={data.data ? { total: data.data.total } : undefined} filters={filters} setFilters={setFilters} />
      </Card>
    </div>
  );
}


function HreflangTab({ crawlId }: { crawlId: number }) {
  const [filters, setFilters] = useState<CrawlSubstrateFilters>({ limit: 200, offset: 0 });
  const data = useCrawlSubstrate(crawlId, "hreflang", filters);
  const setF = (patch: Partial<CrawlSubstrateFilters>) =>
    setFilters((f) => ({ ...f, offset: 0, ...patch }));

  return (
    <div className="space-y-3">
      <Card>
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">Hreflang</h3>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Hreflang annotations per URL. Missing return links, inconsistent
          language/region pairs, and noindex return links also surface as Issues.
        </p>
        <div className="mt-2 relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input type="text" placeholder="filter URL…"
            value={filters.search ?? ""}
            onChange={(e) => setF({ search: e.target.value || undefined })}
            className="w-full rounded-md border border-input bg-background pl-7 pr-2 py-1.5 text-xs" />
        </div>
        {data.data && <RowCount n={data.data.items.length} total={data.data.total} />}
      </Card>

      <Card className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="border-b border-border text-left">
            <tr>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">URL</th>
              <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Hreflang count</th>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Indexability</th>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Hreflang annotations (raw)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.isLoading && <tr><td colSpan={4} className="px-2 py-4 text-center text-muted-foreground">Loading…</td></tr>}
            {data.data?.items.map((r) => {
              // raw blob has every hreflang-* column Frog exports
              const hreflangs = r.raw
                ? Object.entries(r.raw).filter(([k]) => k.toLowerCase().startsWith("hreflang") || k.toLowerCase().includes("language"))
                : [];
              return (
                <tr key={r.id} className="hover:bg-accent/40">
                  <td className="px-2 py-1.5 align-top font-mono text-[11px] max-w-[30%] truncate">
                    <UrlLink url={r.url} />
                  </td>
                  <td className="px-2 py-1.5 text-right align-top font-mono">{r.occurrences ?? "—"}</td>
                  <td className="px-2 py-1.5 align-top">
                    <div>{r.indexability ?? "—"}</div>
                    {r.indexability_status && <div className="text-[10px] text-muted-foreground">{r.indexability_status}</div>}
                  </td>
                  <td className="px-2 py-1.5 align-top max-w-[40%]">
                    {hreflangs.length > 0 ? (
                      <ul className="space-y-0.5 text-[10px] font-mono">
                        {hreflangs.slice(0, 8).map(([k, v]) => (
                          <li key={k}><span className="text-muted-foreground">{k}:</span> {String(v).slice(0, 60)}</li>
                        ))}
                      </ul>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                </tr>
              );
            })}
            {data.data && data.data.items.length === 0 && (
              <tr><td colSpan={4} className="px-2 py-4 text-center text-muted-foreground">No hreflang rows.</td></tr>
            )}
          </tbody>
        </table>
        <Paginator data={data.data ? { total: data.data.total } : undefined} filters={filters} setFilters={setFilters} />
      </Card>
    </div>
  );
}


function SchemaTab({ crawlId }: { crawlId: number }) {
  const [filters, setFilters] = useState<CrawlSubstrateFilters>({ limit: 200, offset: 0 });
  const data = useCrawlSubstrate(crawlId, "schema", filters);
  const setF = (patch: Partial<CrawlSubstrateFilters>) =>
    setFilters((f) => ({ ...f, offset: 0, ...patch }));

  return (
    <div className="space-y-3">
      <Card>
        <div className="flex items-center gap-2">
          <Code className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">Structured data</h3>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Schema.org validation per URL. Errors + warnings + types declared.
          Pages with errors won't qualify for rich-result eligibility.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input type="text" placeholder="filter URL…"
              value={filters.search ?? ""}
              onChange={(e) => setF({ search: e.target.value || undefined })}
              className="w-full rounded-md border border-input bg-background pl-7 pr-2 py-1.5 text-xs" />
          </div>
          <SrcFilterBtn label="Errors only"
            active={!!filters.only_errors}
            onClick={() => setF({ only_errors: filters.only_errors ? undefined : true })} />
        </div>
        {data.data && <RowCount n={data.data.items.length} total={data.data.total} />}
      </Card>

      <Card className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="border-b border-border text-left">
            <tr>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">URL</th>
              <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Errors</th>
              <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Warnings</th>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Schema types</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.isLoading && <tr><td colSpan={4} className="px-2 py-4 text-center text-muted-foreground">Loading…</td></tr>}
            {data.data?.items.map((r) => (
              <tr key={r.id} className={cn("hover:bg-accent/40", (r.errors ?? 0) > 0 && "bg-red-500/5")}>
                <td className="px-2 py-1.5 align-top font-mono text-[11px] max-w-[40%] truncate">
                  <UrlLink url={r.url} />
                </td>
                <td className="px-2 py-1.5 text-right align-top font-mono">
                  {(r.errors ?? 0) > 0 ? <span className="text-red-700 dark:text-red-400">{r.errors}</span> : r.errors ?? "—"}
                </td>
                <td className="px-2 py-1.5 text-right align-top font-mono">
                  {(r.warnings ?? 0) > 0 ? <span className="text-amber-700 dark:text-amber-400">{r.warnings}</span> : r.warnings ?? "—"}
                </td>
                <td className="px-2 py-1.5 align-top max-w-[30%]">
                  {r.types ? <span className="font-mono text-[10px]">{r.types}</span> : <span className="text-muted-foreground">—</span>}
                </td>
              </tr>
            ))}
            {data.data && data.data.items.length === 0 && (
              <tr><td colSpan={4} className="px-2 py-4 text-center text-muted-foreground">No schema rows.</td></tr>
            )}
          </tbody>
        </table>
        <Paginator data={data.data ? { total: data.data.total } : undefined} filters={filters} setFilters={setFilters} />
      </Card>
    </div>
  );
}


function MobileTab({ crawlId }: { crawlId: number }) {
  const [filters, setFilters] = useState<CrawlSubstrateFilters>({ limit: 200, offset: 0 });
  const data = useCrawlSubstrate(crawlId, "mobile", filters);
  const setF = (patch: Partial<CrawlSubstrateFilters>) =>
    setFilters((f) => ({ ...f, offset: 0, ...patch }));

  return (
    <div className="space-y-3">
      <Card>
        <div className="flex items-center gap-2">
          <Smartphone className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">Mobile</h3>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Per-URL mobile signals — viewport meta tag, indexability. Missing
          viewport pages fail mobile-first SERP eligibility.
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input type="text" placeholder="filter URL…"
              value={filters.search ?? ""}
              onChange={(e) => setF({ search: e.target.value || undefined })}
              className="w-full rounded-md border border-input bg-background pl-7 pr-2 py-1.5 text-xs" />
          </div>
          <SrcFilterBtn label="Missing viewport"
            active={!!filters.only_missing}
            onClick={() => setF({ only_missing: filters.only_missing ? undefined : true })} />
        </div>
        {data.data && <RowCount n={data.data.items.length} total={data.data.total} />}
      </Card>

      <Card className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="border-b border-border text-left">
            <tr>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">URL</th>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Viewport</th>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Indexability</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.isLoading && <tr><td colSpan={3} className="px-2 py-4 text-center text-muted-foreground">Loading…</td></tr>}
            {data.data?.items.map((r) => (
              <tr key={r.id} className={cn("hover:bg-accent/40", (!r.viewport || r.viewport === "") && "bg-red-500/5")}>
                <td className="px-2 py-1.5 align-top font-mono text-[11px] max-w-[40%] truncate">
                  <UrlLink url={r.url} />
                </td>
                <td className="px-2 py-1.5 align-top font-mono text-[10px]">
                  {r.viewport
                    ? <span title={r.viewport}>{r.viewport.slice(0, 80)}</span>
                    : <span className="italic text-red-700 dark:text-red-400">missing</span>}
                </td>
                <td className="px-2 py-1.5 align-top">{r.indexability ?? "—"}</td>
              </tr>
            ))}
            {data.data && data.data.items.length === 0 && (
              <tr><td colSpan={3} className="px-2 py-4 text-center text-muted-foreground">No mobile rows.</td></tr>
            )}
          </tbody>
        </table>
        <Paginator data={data.data ? { total: data.data.total } : undefined} filters={filters} setFilters={setFilters} />
      </Card>
    </div>
  );
}


function SecurityTab({ crawlId }: { crawlId: number }) {
  const [filters, setFilters] = useState<CrawlSubstrateFilters>({ limit: 200, offset: 0 });
  const data = useCrawlSubstrate(crawlId, "security", filters);
  const setF = (patch: Partial<CrawlSubstrateFilters>) =>
    setFilters((f) => ({ ...f, offset: 0, ...patch }));

  const byKind = data.data?.by_filter ?? {};
  const kindLabels: Record<string, string> = {
    http_url:          "HTTP URLs",
    mixed_content:     "Mixed content",
    insecure_form:     "Insecure forms",
    unsafe_xorigin:    "Unsafe cross-origin",
    protocol_relative: "Protocol-relative",
    insecure_content:  "Insecure content",
  };

  return (
    <div className="space-y-3">
      <Card>
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">Security</h3>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          HTTP URLs, mixed content, insecure forms, unsafe cross-origin
          links, protocol-relative outlinks. Each kind is a separate Frog
          export type — filter by category to drill in.
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <FilterPill active={!filters.security_kind} onClick={() => setF({ security_kind: undefined })}>
            All
          </FilterPill>
          {Object.entries(byKind).map(([k, n]) => (
            <FilterPill key={k} active={filters.security_kind === k}
              onClick={() => setF({ security_kind: k })}>
              {kindLabels[k] ?? k} <CountChip n={n} />
            </FilterPill>
          ))}
        </div>
        {data.data && <RowCount n={data.data.items.length} total={data.data.total} />}
      </Card>

      <Card className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="border-b border-border text-left">
            <tr>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Kind</th>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">URL</th>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Source</th>
              <th className="px-2 py-1.5 font-medium text-muted-foreground">Target</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.isLoading && <tr><td colSpan={4} className="px-2 py-4 text-center text-muted-foreground">Loading…</td></tr>}
            {data.data?.items.map((r) => (
              <tr key={r.id} className="hover:bg-accent/40">
                <td className="px-2 py-1.5 align-top">
                  <span className="rounded bg-red-500/10 px-1.5 py-0.5 text-[10px] font-medium text-red-700 dark:text-red-400">
                    {kindLabels[r.security_kind ?? ""] ?? r.security_kind ?? "—"}
                  </span>
                </td>
                <td className="px-2 py-1.5 align-top font-mono text-[11px] max-w-[30%] truncate">
                  <UrlLink url={r.url} />
                </td>
                <td className="px-2 py-1.5 align-top font-mono text-[11px] max-w-[30%] truncate">
                  <UrlLink url={r.source_url} />
                </td>
                <td className="px-2 py-1.5 align-top font-mono text-[11px] max-w-[30%] truncate">
                  <UrlLink url={r.target_url} />
                </td>
              </tr>
            ))}
            {data.data && data.data.items.length === 0 && (
              <tr><td colSpan={4} className="px-2 py-4 text-center text-muted-foreground">No security rows.</td></tr>
            )}
          </tbody>
        </table>
        <Paginator data={data.data ? { total: data.data.total } : undefined} filters={filters} setFilters={setFilters} />
      </Card>
    </div>
  );
}


function OrphansTab({ crawlId }: { crawlId: number }) {
  const [filters, setFilters] = useState<CrawlSubstrateFilters>({ limit: 200, offset: 0 });
  const data = useCrawlSubstrate(crawlId, "orphans", filters);
  const setF = (patch: Partial<CrawlSubstrateFilters>) =>
    setFilters((f) => ({ ...f, offset: 0, ...patch }));

  return (
    <div className="space-y-3">
      <Card>
        <div className="flex items-center gap-2">
          <FileWarning className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">Orphan pages</h3>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          URLs known to GA4 / GSC / sitemap but with no internal inlinks.
          Each is either a page that needs adding to the navigation or one
          that should be removed/canonicalised.
        </p>
        <div className="mt-2 relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input type="text" placeholder="filter URL…"
            value={filters.search ?? ""}
            onChange={(e) => setF({ search: e.target.value || undefined })}
            className="w-full rounded-md border border-input bg-background pl-7 pr-2 py-1.5 text-xs" />
        </div>
        {data.data && <RowCount n={data.data.items.length} total={data.data.total} />}
      </Card>

      <Card className="overflow-x-auto">
        {data.data && data.data.items.length === 0 && !data.isLoading ? (
          <div className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
            No orphan pages reported. Orphans require Frog to be configured to
            pull GA4 / GSC / Sitemap data alongside the crawl (Configuration →
            API Access). Without that, this report stays empty even when
            orphans exist.
          </div>
        ) : (
          <table className="min-w-full text-xs">
            <thead className="border-b border-border text-left">
              <tr>
                <th className="px-2 py-1.5 font-medium text-muted-foreground">URL</th>
                <th className="px-2 py-1.5 font-medium text-muted-foreground">Source</th>
                <th className="px-2 py-1.5 text-right font-medium text-muted-foreground">Inlinks</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.isLoading && <tr><td colSpan={3} className="px-2 py-4 text-center text-muted-foreground">Loading…</td></tr>}
              {data.data?.items.map((r) => (
                <tr key={r.id} className="hover:bg-accent/40">
                  <td className="px-2 py-1.5 align-top font-mono text-[11px] max-w-[55%] truncate">
                    <UrlLink url={r.url} />
                  </td>
                  <td className="px-2 py-1.5 align-top">{r.source ?? "—"}</td>
                  <td className="px-2 py-1.5 text-right align-top font-mono">{r.inlinks ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Paginator data={data.data ? { total: data.data.total } : undefined} filters={filters} setFilters={setFilters} />
      </Card>
    </div>
  );
}


// ─── helpers shared by substrate tabs ─────────────────────────────


function UrlLink({ url }: { url: string | null | undefined }) {
  if (!url) return <span className="text-muted-foreground">—</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer"
       className="hover:underline" title={url}>
      {stripScheme(url)}
    </a>
  );
}


function RowCount({ n, total }: { n: number; total: number }) {
  return (
    <div className="mt-2 text-xs text-muted-foreground">
      Showing {n.toLocaleString()} of {total.toLocaleString()}
    </div>
  );
}




// ─── Helpers ──────────────────────────────────────────────────────


function Paginator<F extends { limit?: number; offset?: number }>({
  data, filters, setFilters,
}: {
  data: { total: number } | undefined;
  filters: F;
  setFilters: React.Dispatch<React.SetStateAction<F>>;
}) {
  if (!data) return null;
  const limit = filters.limit ?? 100;
  if (data.total <= limit) return null;
  return (
    <div className="mt-3 flex items-center justify-between gap-2 text-xs">
      <span className="text-muted-foreground">
        Page {Math.floor((filters.offset ?? 0) / limit) + 1} of {Math.ceil(data.total / limit)}
      </span>
      <div className="flex items-center gap-1">
        <button type="button"
          disabled={(filters.offset ?? 0) === 0}
          onClick={() => setFilters((f) => ({ ...f, offset: Math.max(0, (f.offset ?? 0) - limit) }))}
          className="rounded-md border border-border bg-background px-2 py-1 hover:bg-accent disabled:opacity-50">
          Prev
        </button>
        <button type="button"
          disabled={(filters.offset ?? 0) + limit >= data.total}
          onClick={() => setFilters((f) => ({ ...f, offset: (f.offset ?? 0) + limit }))}
          className="rounded-md border border-border bg-background px-2 py-1 hover:bg-accent disabled:opacity-50">
          Next
        </button>
      </div>
    </div>
  );
}


function SortHeader({ label, col, filters, setF, align, className }: {
  label: string; col: string;
  filters: CrawlPageFilters;
  setF: (patch: Partial<CrawlPageFilters>) => void;
  align?: "right"; className?: string;
}) {
  const active = filters.sort === col;
  const dir = active ? filters.sort_dir : "asc";
  return (
    <th onClick={() => setF({ sort: col, sort_dir: active && dir === "asc" ? "desc" : "asc", offset: 0 })}
      className={cn(
        "cursor-pointer select-none px-2 py-1.5 font-medium text-muted-foreground hover:text-foreground",
        align === "right" && "text-right", className,
      )}>
      <span className="inline-flex items-center gap-0.5">
        {label}
        {active && (dir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
      </span>
    </th>
  );
}


function PageRow({ page: p }: { page: CrawlPage }) {
  const statusKind = !p.status_code ? undefined
                   : p.status_code >= 500 ? "err"
                   : p.status_code >= 400 ? "warn"
                   : p.status_code >= 300 ? "info"
                   : "ok";
  return (
    <tr className="hover:bg-accent/40">
      <td className="px-2 py-1.5 align-top">
        <a href={p.url} target="_blank" rel="noreferrer"
           className="inline-flex items-start gap-1 truncate font-mono text-xs hover:underline" title={p.url}>
          <span className="truncate">{stripScheme(p.url)}</span>
          <ExternalLink className="mt-0.5 h-3 w-3 shrink-0 opacity-50" />
        </a>
      </td>
      <td className="px-2 py-1.5 align-top">
        {p.status_code != null && (
          <span className={cn(
            "rounded px-1 font-mono",
            statusKind === "ok"   && "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
            statusKind === "info" && "bg-blue-500/10 text-blue-700 dark:text-blue-400",
            statusKind === "warn" && "bg-amber-500/10 text-amber-700 dark:text-amber-400",
            statusKind === "err"  && "bg-red-500/10 text-red-700 dark:text-red-400",
          )}>{p.status_code}</span>
        )}
      </td>
      <td className="px-2 py-1.5 align-top">
        {p.title
          ? <span className="line-clamp-2 break-words" title={p.title}>{p.title}</span>
          : <span className="italic text-muted-foreground">missing</span>
        }
        {!p.meta_description && <div className="text-[10px] italic text-muted-foreground">no meta</div>}
      </td>
      <td className="px-2 py-1.5 text-right align-top">{p.word_count?.toLocaleString() ?? "—"}</td>
      <td className="px-2 py-1.5 text-right align-top">{p.inlinks?.toLocaleString() ?? "—"}</td>
      <td className="px-2 py-1.5 text-right align-top">{p.outlinks?.toLocaleString() ?? "—"}</td>
      <td className="px-2 py-1.5 text-right align-top">{p.crawl_depth ?? "—"}</td>
      <td className="px-2 py-1.5 text-right align-top">{p.response_time_ms ?? "—"}</td>
      <td className="px-2 py-1.5 align-top">
        <div className="text-[11px]">{p.indexability ?? "—"}</div>
        {p.indexability_status && (
          <div className="text-[10px] text-muted-foreground">{p.indexability_status}</div>
        )}
      </td>
    </tr>
  );
}


function FilterTriToggle({ label, value, onTrue, onFalse, onChange }: {
  label: string;
  value: boolean | undefined;
  onTrue: string;
  onFalse: string;
  onChange: (v: boolean | undefined) => void;
}) {
  const cycle = () => {
    if (value === undefined) onChange(true);
    else if (value === true) onChange(false);
    else onChange(undefined);
  };
  const display = value === undefined ? "any" : value === true ? onTrue : onFalse;
  return (
    <button type="button" onClick={cycle}
      className={cn(
        "rounded-md border px-2 py-1.5 text-xs",
        value === undefined ? "border-input bg-background" :
        value === true ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400" :
                         "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
      )}>
      {label}: {display}
    </button>
  );
}


function SrcFilterBtn({ label, active, onClick }: {
  label: string; active: boolean; onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick}
      className={cn(
        "rounded-md border px-2 py-1.5 text-xs",
        active ? "border-primary bg-primary/10 text-primary" : "border-input bg-background hover:bg-accent",
      )}>
      {label}
    </button>
  );
}


function stripScheme(url: string): string {
  return url.replace(/^https?:\/\//, "").replace(/^www\./, "");
}
